import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, TextField, Typography } from '@mui/material';
import ConfigCard from './ConfigCard';
import { ChipsBox, ChipToggle, AvailableChip, SubLabel } from './_PdiHelpers';
import { useWebshopLanguages } from '../../WebshopLanguagesContext';
import { LanguageBar } from '../../_LanguageBar';

const ALL_LANGS = [
    { code: 'nl', short: 'NL', label: 'Nederlands' },
    { code: 'fr', short: 'FR', label: 'Français'   },
    { code: 'en', short: 'EN', label: 'English'    },
];

// "01,02" ↔ ['01','02'] for one language slot.
const parseList = (csv) => String(csv || '').split(',').map(s => s.trim()).filter(Boolean);

// One chip zone: chips in stored (= render) order with ×-remove, plus its own
// "available" row of remaining types. Module scope so React keeps its identity
// across renders (declared inline it would remount on every keystroke).
const ChipZone = ({ csv, label, emptyText, textTypes, nameFor, onCsvChange }) => {
    const list = parseList(csv);
    const available = textTypes.filter(tt => !list.includes(String(tt.id)));
    return (
        <Box>
            <SubLabel>{label}</SubLabel>
            <ChipsBox>
                {list.map(code => (
                    <ChipToggle
                        key={code}
                        label={nameFor(code)}
                        onRemove={() => onCsvChange(list.filter(c => c !== code).join(','))}
                    />
                ))}
                {list.length === 0 && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', px: 1, py: 0.5 }}>
                        {emptyText}
                    </Typography>
                )}
            </ChipsBox>
            {available.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                    {available.map(tt => (
                        <AvailableChip
                            key={tt.id}
                            label={tt.name}
                            onAdd={() => onCsvChange([...list, String(tt.id)].join(','))}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};
ChipZone.propTypes = {
    csv:         PropTypes.string,
    label:       PropTypes.node,
    emptyText:   PropTypes.node,
    textTypes:   PropTypes.array,
    nameFor:     PropTypes.func,
    onCsvChange: PropTypes.func,
};

/**
 * Per-language product-text types — wired to the legacy settings the
 * storefront actually reads (Product.php getById):
 *
 *   PRODUCT_TEXTS_LOCALE      = "<nl>;<en>;<fr>"  → text type(s) rendered in
 *                               the description block on the product page
 *   PRODUCT_TEXTS_META_LOCALE = "<nl>;<en>;<fr>"  → text type(s) parsed for
 *                               the meta fields
 *
 * Each slot is a comma-list of POS text-type codes (customer-specific, e.g.
 * "01"); order = render order on the product page. `textTypes` ([{id, name}],
 * from www_textpreset via the mini-api /admin/text-types proxy) supplies the
 * readable names for the chips; when the list is empty (POS API unreachable
 * or mini-api not yet deployed) the card falls back to raw code inputs.
 */
const DescriptionTextsPerLanguageCard = ({ value = {}, onChange, textTypes = [], theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    // Only show language tabs the admin is allowed to edit (ADMIN_LANGUAGES).
    const { adminLanguages } = useWebshopLanguages();
    const LANGS = ALL_LANGS.filter(l => adminLanguages.includes(String(l.code).toUpperCase()));
    const [tab, setTab] = useState(0);
    const activeTab = (tab >= 0 && tab < LANGS.length) ? tab : 0;
    const lang = (LANGS[activeTab] || ALL_LANGS[0]).code;
    const langData = value[lang] || { page: '', meta: '' };

    const setField = (field, newValue) => {
        if (!onChange) return;
        onChange(lang, { ...langData, [field]: newValue });
    };

    const copyFrom = (fromCode) => {
        if (!onChange || fromCode === lang) return;
        const src = value[fromCode] || { page: '', meta: '' };
        onChange(lang, { page: src.page || '', meta: src.meta || '' });
    };

    const nameFor = (code) => {
        const match = textTypes.find(tt => String(tt.id) === code);
        return match ? match.name : code;
    };

    const hasTypeList = textTypes.length > 0;

    return (
        <ConfigCard
            anchor="cfg-card-pdi-descriptions"
            title={tr('cfg_pdi_dtl_title', 'Product description texts per language')}
            description={tr('cfg_pdi_dtl_desc2', 'Which POS texts are shown per language: on the product page (in order) and for the meta fields. Empty = no texts for that language.')}
            theme={theme}
        >
            <LanguageBar
                langs={LANGS}
                activeCode={lang}
                onSelect={(code) => setTab(LANGS.findIndex(l => l.code === code))}
                onCopyFrom={copyFrom}
                tr={tr}
            />
            {hasTypeList ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <ChipZone
                        csv={langData.page}
                        label={tr('cfg_pdi_dtl_on_page', 'Teksten op de productpagina (in volgorde)')}
                        emptyText={tr('cfg_pdi_dtl_empty_zone', 'Nog geen teksttypes — kies hieronder uit de beschikbare types.')}
                        textTypes={textTypes}
                        nameFor={nameFor}
                        onCsvChange={(csv) => setField('page', csv)}
                    />
                    <ChipZone
                        csv={langData.meta}
                        label={tr('cfg_pdi_dtl_meta', 'Teksten voor meta & extra velden')}
                        emptyText={tr('cfg_pdi_dtl_empty_zone', 'Nog geen teksttypes — kies hieronder uit de beschikbare types.')}
                        textTypes={textTypes}
                        nameFor={nameFor}
                        onCsvChange={(csv) => setField('meta', csv)}
                    />
                </Box>
            ) : (
                // POS type list unavailable — raw code inputs so the settings
                // stay editable (comma-list of POS text-type codes).
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <TextField
                        label={tr('cfg_pdi_dtl_page_type', 'Tekst op de productpagina (type)')}
                        size="small"
                        value={langData.page || ''}
                        onChange={(e) => setField('page', e.target.value)}
                        placeholder="01"
                        helperText={tr('cfg_pdi_dtl_page_type_help', 'POS-teksttype voor de omschrijving. Meerdere types kan met een komma (bv. 01,02).')}
                    />
                    <TextField
                        label={tr('cfg_pdi_dtl_meta_type', 'Meta-velden (type)')}
                        size="small"
                        value={langData.meta || ''}
                        onChange={(e) => setField('meta', e.target.value)}
                        placeholder="00"
                        helperText={tr('cfg_pdi_dtl_meta_type_help', 'POS-teksttype waaruit de meta-velden gelezen worden.')}
                    />
                </Box>
            )}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {tr('cfg_pdi_dtl_codes_hint', 'De type-codes komen uit het kassasysteem van de klant (Teksten bij artikel).')}
            </Typography>
        </ConfigCard>
    );
};

DescriptionTextsPerLanguageCard.propTypes = {
    value:     PropTypes.object,
    onChange:  PropTypes.func,
    textTypes: PropTypes.array,
    theme:     PropTypes.string,
    t:         PropTypes.func,
};

export default DescriptionTextsPerLanguageCard;
